import React from 'react'
import CircularProgress from '@material-ui/core/CircularProgress'
// @src imports
import { Confirm, Icon } from 'src/chrome'
import { PartialLine } from 'src/helpers/multitouch'
import * as SendDelayOptions from 'src/orders/sendDelayOptions'
import styles from 'src/chrome/styles/detailView.module.scss'
import lineStyles from './multiLineRibbon.module.scss'
import { useActions, useSelector, useState } from 'src/hooks'
import { Text } from '@sendoutcards/quantum-design-ui'

type MultiLineRibbonProps = {
  isLineCardImageLoading: boolean
  onRemoveLine: (line: PartialLine) => void
  onCardSelect: (line: PartialLine, index: number) => void
  onImageLoaded: (arg: string) => void
  onImageError: () => void
  onAddLine: () => void
}

const MultiLineRibbon: React.FC<MultiLineRibbonProps> = props => {
  const {
    onRemoveLine,
    onAddLine,
    isLineCardImageLoading,
    onCardSelect,
    onImageLoaded,
    onImageError,
  } = props

  const { order, activeLineIndex } = useSelector(state => state.orders)

  const actions = useActions()

  const [isRemoveCardAlertVisible, setIsRemoveCardAlertVisible] = useState(
    false,
  )
  const [lineToRemove, setLineToRemove] = useState<PartialLine | null>(null)

  const toggleRemoveAlert = (event?: React.MouseEvent, line?: PartialLine) => {
    if (event) {
      event.stopPropagation()
    }

    setIsRemoveCardAlertVisible(!isRemoveCardAlertVisible)
    setLineToRemove(line ?? null)
  }

  const handleRemoveLine = (line: PartialLine) => {
    if (!order) {
      return
    }

    if (order.lines.length > 1) {
      if (lineToRemove) {
        onRemoveLine(lineToRemove)
      }
    } else {
      actions.clearOrder()
      actions.openCatalog()
    }

    setIsRemoveCardAlertVisible(false)
    setLineToRemove(null)
  }

  const handleAddLine = () => {
    // check if account has a subscription
    // Update - 1/21/21: Removed logic to add upsale, now all
    // basic accounts can use this functionality for free as well.
    onAddLine()
  }

  return (
    <section
      className={`${styles.previewSection} ${styles.overviewCard}`}
      style={{ flexDirection: 'column' }}
    >
      <div
        style={{
          marginBottom: '15px',
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Text type="largeBody" color="primaryHeading">
          Cards Attached To Your Order
        </Text>
      </div>
      <div className={lineStyles.ribbon}>
        {isLineCardImageLoading && (
          <div className={styles.loader}>
            <CircularProgress />
          </div>
        )}
        {order &&
          order.lines &&
          order.lines.map((line, index) => {
            return (
              <div
                key={index}
                style={
                  line.card?.isHorizontal ? { width: 126.2 } : { width: 90.4 }
                }
                className={
                  activeLineIndex === index
                    ? `${lineStyles.cardContainer} ${lineStyles.active}`
                    : lineStyles.cardContainer
                }
              >
                <div
                  className={lineStyles.card}
                  onClick={() => onCardSelect(line, index)}
                >
                  <img
                    style={{ display: 'none' }}
                    alt="Used for onLoad capabilities to set/unset loading icon"
                    src={line.card?.frontPreviewUrl ?? ''}
                    onLoad={() => onImageLoaded('lineCard')}
                    onError={onImageError}
                  />
                  <div
                    className={
                      activeLineIndex === index
                        ? line.card?.isHorizontal
                          ? `${lineStyles.landscape} ${lineStyles.active}`
                          : `${lineStyles.portrait} ${lineStyles.active}`
                        : line.card?.isHorizontal
                        ? lineStyles.landscape
                        : lineStyles.portrait
                    }
                    style={{
                      display: 'flex',
                      backgroundImage: line.card
                        ? `url(${line.card.frontPreviewUrl})`
                        : '',
                    }}
                  >
                    <button
                      className={lineStyles.closeButton}
                      onClick={e => toggleRemoveAlert(e, line)}
                    >
                      <Icon icon={'CLOSE'} color="#F262B1" size={13} />
                    </button>
                  </div>
                  {line.giftVariation && (
                    <div
                      className={
                        activeLineIndex === index
                          ? `${lineStyles.active} ${lineStyles.gift}`
                          : lineStyles.gift
                      }
                      style={{
                        backgroundImage: `url(${line.giftVariation.defaultImageUrl})`,
                      }}
                    />
                  )}
                </div>
                <div className={lineStyles.delayDescription}>
                  <Text type="caption" color="primaryBrand" alignment="center">
                    {SendDelayOptions.ribbonDescription(
                      line.sendDelay,
                      'MMM Do, YYYY',
                    )}
                  </Text>
                </div>
              </div>
            )
          })}
        <div className={lineStyles.addContainer}>
          <div className={lineStyles.addFlex}>
            <button
              id={'add_card_to_order_btn'}
              className={lineStyles.addButton}
              onClick={handleAddLine}
            >
              <Icon icon={'ADD'} color="#F262B1" size={30} />
            </button>
            <div>
              <Text type="caption" color="primaryHeading" alignment="center">
                Add a Card
              </Text>
            </div>
          </div>
        </div>
      </div>
      {isRemoveCardAlertVisible && lineToRemove && (
        <Confirm
          title={'Remove card from order?'}
          message={'Are you sure you want to remove this item from your order?'}
          confirmTitle={'Remove'}
          onConfirm={() => handleRemoveLine(lineToRemove)}
          onDecline={() => toggleRemoveAlert()}
        />
      )}
    </section>
  )
}

export default MultiLineRibbon
